import React from 'react'
import { motion, type Variants, useReducedMotion } from 'framer-motion'

type BaseProps = {
  children: React.ReactNode
  className?: string
  delay?: number
  once?: boolean
}

type ImageProps = BaseProps & {
  disableHover?: boolean
}

const easeOut: [number, number, number, number] = [0.22, 1, 0.36, 1]

export function useMotionVariants() {
  const reduce = useReducedMotion()

  const reveal: Variants = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 },
    show: reduce
      ? { opacity: 1, y: 0 }
      : {
          opacity: 1,
          y: 0,
          transition: { duration: 0.6, ease: easeOut },
        },
  }

  const card: Variants = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 14, scale: 0.98 },
    show: reduce
      ? { opacity: 1, y: 0, scale: 1 }
      : {
          opacity: 1,
          y: 0,
          scale: 1,
          transition: { duration: 0.55, ease: easeOut },
        },
  }

  const image: Variants = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 },
    show: reduce
      ? { opacity: 1, y: 0 }
      : {
          opacity: 1,
          y: 0,
          transition: { duration: 0.65, ease: easeOut },
        },
  }

  return { reveal, card, image, reduce }
}

export function MotionSection({ children, className, delay = 0, once = true }: BaseProps) {
  const { reveal, reduce } = useMotionVariants()

  return (
    <motion.section
      className={className}
      variants={reveal}
      initial="hidden"
      whileInView="show"
      viewport={{ once, amount: 0.25 }}
      transition={reduce ? undefined : { delay }}
    >
      {children}
    </motion.section>
  )
}

export function MotionDiv({ children, className, delay = 0, once = true }: BaseProps) {
  const { reveal, reduce } = useMotionVariants()

  return (
    <motion.div
      className={className}
      variants={reveal}
      initial="hidden"
      whileInView="show"
      viewport={{ once, amount: 0.25 }}
      transition={reduce ? undefined : { delay }}
    >
      {children}
    </motion.div>
  )
}

export function MotionCard({ children, className, delay = 0, once = true, onClick }: BaseProps & { onClick?: () => void }) {
  const { card, reduce } = useMotionVariants()

  return (
    <motion.div
      className={className}
      variants={card}
      initial="hidden"
      whileInView="show"
      viewport={{ once, amount: 0.25 }}
      transition={reduce ? undefined : { delay }}
      whileHover={reduce ? undefined : { y: -4, scale: 1.01 }}
      whileTap={reduce ? undefined : { scale: 0.99 }}
      onClick={onClick}
    >
      {children}
    </motion.div>
  )
}

export function MotionImage({ children, className, delay = 0, once = true, disableHover = false }: ImageProps) {
  const { image, reduce } = useMotionVariants()

  return (
    <motion.div
      className={className}
      variants={image}
      initial="hidden"
      whileInView="show"
      viewport={{ once, amount: 0.25 }}
      transition={reduce ? undefined : { delay }}
      whileHover={reduce || disableHover ? undefined : { scale: 1.02 }}
    >
      {children}
    </motion.div>
  )
}

export function MotionIcon({ children, className }: { children: React.ReactNode; className?: string }) {
  const reduce = useReducedMotion()

  return (
    <motion.span
      className={className}
      whileHover={reduce ? undefined : { scale: 1.12, rotate: -2 }}
      transition={reduce ? undefined : { duration: 0.18, ease: easeOut }}
    >
      {children}
    </motion.span>
  )
}

export function MotionLink({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  const reduce = useReducedMotion()

  return (
    <motion.span
      className={className}
      whileHover={reduce ? undefined : { y: -1 }}
      whileTap={reduce ? undefined : { scale: 0.98 }}
      transition={reduce ? undefined : { duration: 0.16, ease: easeOut }}
    >
      {children}
    </motion.span>
  )
}

export function MotionButton({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  const reduce = useReducedMotion()

  return (
    <motion.span
      className={className}
      whileHover={reduce ? undefined : { y: -1, scale: 1.01 }}
      whileTap={reduce ? undefined : { scale: 0.98 }}
      transition={reduce ? undefined : { duration: 0.16, ease: easeOut }}
    >
      {children}
    </motion.span>
  )
}
